<template>
  <button
    type="submit"
    :class="buttonClass"
    :disabled="props.disabled"
    :style="{
      height: props.height,
      lineHeight: props.lineHeight,
      minWidth: props.minWidth,
    }"
    @click.prevent="props.onClick"
  >
    <slot></slot>
  </button>
</template>

<script setup lang="ts">
import { tv } from 'tailwind-variants';

interface Props {
  disabled?: boolean;
  onClick: () => void;
  height?: string;
  lineHeight?: string;
  minWidth?: string;
  theme?: 'light' | 'dark';
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  height: '50px',
  lineHeight: '40px',
  minWidth: '120px',
  theme: 'light',
});

const buttonStyle = tv({
  base: 'w-full cursor-pointer self-center break-keep rounded-[1px] border-[1px] border-solid px-4 py-0 text-base transition-all duration-300 ease-linear focus:outline-none',
  variants: {
    disabled: {
      true: 'cursor-default',
      false: '',
    },
    theme: {
      light: '',
      dark: '',
    },
  },
  compoundVariants: [
    // Light + Enabled
    {
      disabled: false,
      theme: 'light',
      class: 'border-transparent text-light-fg-inverse light-bg-accent hover:border-light-bd-accent hover:bg-light-bg-default hover:bg-none hover:text-light-fg-accent',
    },
    // Dark + Enabled
    {
      disabled: false,
      theme: 'dark',
      class: 'border-transparent text-dark-fg-inverse dark-bg-accent hover:border-dark-bd-accent hover:bg-dark-bg-default hover:bg-none hover:text-dark-fg-accent',
    },
    // Light + Disabled
    {
      disabled: true,
      theme: 'light',
      class: 'border-light-bd-disabled bg-light-bg-disabled text-light-fg-disabled',
    },
    // Dark + Disabled
    {
      disabled: true,
      theme: 'dark',
      class: 'border-dark-bd-disabled bg-dark-bg-disabled text-dark-fg-disabled',
    },
  ],
});

const buttonClass = computed(() => buttonStyle({ disabled: props.disabled, theme: props.theme }));
</script>
